import { createUnifiedTheme, genPageTheme, palettes, shapes } from '@backstage/theme';

// WSA brand colors extracted from wsa.com
const wsaColors = {
  loudIndigo: '#8000FF', // hsl(266, 100%, 50%) - primary brand
  deepIndigo: '#290066', // hsl(262, 100%, 20%) - dark variant
  white: '#FFFFFF',
  black: '#504F64', // hsl(243, 12%, 35%)
  grey0: '#F5F3F1', // hsl(30, 11%, 96%)
  grey1: '#D9D9D9', // hsl(0, 0%, 85%)
  grey2: '#BDBDBD', // hsl(180, 1%, 74%)
  grey3: '#7E7C99', // hsl(231, 8%, 52%)
  grey4: '#504F64', // hsl(243, 12%, 35%)
  blue: '#47C1E8', // hsl(193, 87%, 62%)
  green: '#00B38A', // hsl(170, 100%, 35%)
  orange: '#FF9A1F', // hsl(30, 100%, 56%)
  red: '#FF4D4D', // hsl(3, 100%, 65%)
  error: '#D30000',
};

export const wsaLightTheme = createUnifiedTheme({
  palette: {
    ...palettes.light,
    primary: {
      main: wsaColors.loudIndigo,
      light: '#A64DFF',
      dark: wsaColors.deepIndigo,
    },
    secondary: {
      main: wsaColors.deepIndigo,
      light: wsaColors.loudIndigo,
      dark: '#1A0044',
    },
    error: {
      main: wsaColors.error,
    },
    warning: {
      main: wsaColors.orange,
    },
    info: {
      main: wsaColors.blue,
    },
    success: {
      main: wsaColors.green,
    },
    background: {
      default: wsaColors.grey0,
      paper: wsaColors.white,
    },
    banner: {
      info: wsaColors.blue,
      error: wsaColors.error,
      text: wsaColors.white,
      link: wsaColors.white,
      closeButtonColor: wsaColors.white,
      warning: wsaColors.orange,
    },
    navigation: {
      background: wsaColors.deepIndigo,
      indicator: wsaColors.loudIndigo,
      color: wsaColors.white,
      selectedColor: wsaColors.white,
      navItem: {
        hoverBackground: 'rgba(128, 0, 255, 0.3)',
      },
    },
    text: {
      primary: wsaColors.grey4,
      secondary: wsaColors.grey3,
    },
  },
  fontFamily: '"Noto Sans", sans-serif',
  defaultPageTheme: 'home',
  pageTheme: {
    home: genPageTheme({
      colors: [wsaColors.deepIndigo, wsaColors.loudIndigo],
      shape: shapes.wave,
    }),
    documentation: genPageTheme({
      colors: [wsaColors.deepIndigo, wsaColors.loudIndigo],
      shape: shapes.wave2,
    }),
    tool: genPageTheme({
      colors: [wsaColors.deepIndigo, wsaColors.loudIndigo],
      shape: shapes.round,
    }),
    service: genPageTheme({
      colors: [wsaColors.deepIndigo, wsaColors.loudIndigo],
      shape: shapes.wave,
    }),
    website: genPageTheme({
      colors: [wsaColors.deepIndigo, wsaColors.loudIndigo],
      shape: shapes.wave,
    }),
    library: genPageTheme({
      colors: [wsaColors.deepIndigo, wsaColors.loudIndigo],
      shape: shapes.wave,
    }),
    other: genPageTheme({
      colors: [wsaColors.deepIndigo, wsaColors.loudIndigo],
      shape: shapes.wave,
    }),
    app: genPageTheme({
      colors: [wsaColors.deepIndigo, wsaColors.loudIndigo],
      shape: shapes.wave,
    }),
    apis: genPageTheme({
      colors: [wsaColors.deepIndigo, wsaColors.loudIndigo],
      shape: shapes.wave2,
    }),
    'test-service': genPageTheme({
      colors: ['#3B4F6B', '#5A7A9E'],
      shape: shapes.wave,
    }),
  },
  components: {
    MuiButton: {
      styleOverrides: {
        containedPrimary: {
          borderRadius: '100px', // WSA pill-shaped buttons
        },
        outlinedPrimary: {
          borderRadius: '100px',
        },
      },
    },
  },
});

export const wsaDarkTheme = createUnifiedTheme({
  palette: {
    ...palettes.dark,
    primary: {
      main: '#A64DFF',
      light: '#C08FFF',
      dark: wsaColors.loudIndigo,
    },
    secondary: {
      main: wsaColors.loudIndigo,
      light: '#A64DFF',
      dark: wsaColors.deepIndigo,
    },
    error: {
      main: '#FF6B6B',
    },
    warning: {
      main: wsaColors.orange,
    },
    info: {
      main: wsaColors.blue,
    },
    success: {
      main: wsaColors.green,
    },
    background: {
      default: '#1A1A2E',
      paper: '#25253D',
    },
    banner: {
      info: wsaColors.blue,
      error: wsaColors.error,
      text: wsaColors.white,
      link: '#C08FFF',
      closeButtonColor: wsaColors.white,
      warning: wsaColors.orange,
    },
    navigation: {
      background: '#0D0033',
      indicator: wsaColors.loudIndigo,
      color: wsaColors.white,
      selectedColor: wsaColors.white,
      navItem: {
        hoverBackground: 'rgba(128, 0, 255, 0.3)',
      },
    },
    text: {
      primary: '#E0E0E0',
      secondary: '#A0A0B0',
    },
  },
  fontFamily: '"Noto Sans", sans-serif',
  defaultPageTheme: 'home',
  pageTheme: {
    home: genPageTheme({
      colors: [wsaColors.deepIndigo, wsaColors.loudIndigo],
      shape: shapes.wave,
    }),
    documentation: genPageTheme({
      colors: [wsaColors.deepIndigo, wsaColors.loudIndigo],
      shape: shapes.wave2,
    }),
    tool: genPageTheme({
      colors: [wsaColors.deepIndigo, wsaColors.loudIndigo],
      shape: shapes.round,
    }),
    service: genPageTheme({
      colors: [wsaColors.deepIndigo, wsaColors.loudIndigo],
      shape: shapes.wave,
    }),
    website: genPageTheme({
      colors: [wsaColors.deepIndigo, wsaColors.loudIndigo],
      shape: shapes.wave,
    }),
    library: genPageTheme({
      colors: [wsaColors.deepIndigo, wsaColors.loudIndigo],
      shape: shapes.wave,
    }),
    other: genPageTheme({
      colors: [wsaColors.deepIndigo, wsaColors.loudIndigo],
      shape: shapes.wave,
    }),
    app: genPageTheme({
      colors: [wsaColors.deepIndigo, wsaColors.loudIndigo],
      shape: shapes.wave,
    }),
    apis: genPageTheme({
      colors: [wsaColors.deepIndigo, wsaColors.loudIndigo],
      shape: shapes.wave2,
    }),
    'test-service': genPageTheme({
      colors: ['#3B4F6B', '#5A7A9E'],
      shape: shapes.wave,
    }),
  },
  components: {
    MuiButton: {
      styleOverrides: {
        containedPrimary: {
          borderRadius: '100px',
        },
        outlinedPrimary: {
          borderRadius: '100px',
        },
      },
    },
  },
});
